import { getWorkDate } from "@components/inputs/date/utils";
import { IFieldDefFn } from "@components/smart/FieldInfo";
import Tabs from "@components/tabs";
import BindingContext from "@odata/BindingContext";
import {
    EntitySetName,
    IPrAccountAssignmentTemplateEntity,
    IPrAccountingItemTypeEntity,
    IPrPayrollSettingEntity,
    PrAccountAssignmentEntity,
    PrAccountAssignmentTemplateEntity,
    PrAccountingItemTypeEntity,
    PrPayrollSettingEntity
} from "@odata/GeneratedEntityTypes";
import { PrAccountingCategoryCode, PrAccountingItemTypeCode } from "@odata/GeneratedEnums";
import { OData } from "@odata/OData";
import { transformToODataString } from "@odata/OData.utils";
import { getAgendaCountryCode } from "@pages/companies/Company.shared.utils";
import { AccountingTextLocalPath } from "@pages/payroll/accountingSettings/AccountingSettingsDef";
import { logger } from "@utils/log";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import { ValueType } from "../../../enums";
import { TRecordString } from "../../../global.types";
import { hasNestedError } from "../../../views/formView/Form.utils";
import { FormStorage, IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";

export interface IPrAccountingStorageCustomData extends IFormStorageDefaultCustomData {
    prAccountingItemTypes?: IPrAccountingItemTypeEntity[];
    prAccountAssignmentTemplates?: IPrAccountAssignmentTemplateEntity[];
}

async function fetchPrAccountingItemTypes(oData: OData, context: IAppContext, forDate?: Date): Promise<IPrAccountingItemTypeEntity[]> {
    const query = oData.getEntitySetWrapper(EntitySetName.PrAccountingItemTypes).query();

    query.select(PrAccountingItemTypeEntity.Code, PrAccountingItemTypeEntity.Name, PrAccountingItemTypeEntity.ItemGroupCode,
            PrAccountingItemTypeEntity.Order, PrAccountingItemTypeEntity.CountryCode, PrAccountingItemTypeEntity.DateValidFrom, PrAccountingItemTypeEntity.DateValidTo)
            .orderBy(PrAccountingItemTypeEntity.Order);

    const countryCode = transformToODataString(getAgendaCountryCode(context), ValueType.String);
    const filters = [`${PrAccountingItemTypeEntity.CountryCode} eq ${countryCode}`];

    if (forDate) {
        const formattedDate = transformToODataString(forDate, ValueType.Date);
        filters.push(`(${PrAccountingItemTypeEntity.DateValidFrom} le ${formattedDate} OR ${PrAccountingItemTypeEntity.DateValidFrom} eq null)`);
        filters.push(`(${PrAccountingItemTypeEntity.DateValidTo} ge ${formattedDate} OR ${PrAccountingItemTypeEntity.DateValidTo} eq null)`);
    }
    query.filter(filters.join(" AND "));

    const result = await query.fetchData<IPrAccountingItemTypeEntity[]>();
    return result.value;
}

export async function loadPrAccountingItemTypes(storage: FormStorage<IPrPayrollSettingEntity, IPrAccountingStorageCustomData>): Promise<void> {
    const itemTypes = await fetchPrAccountingItemTypes(storage.oData, storage.context, getWorkDate());
    storage.setCustomData({ prAccountingItemTypes: itemTypes });
}

async function fetchPrAccountAssignmentTemplates(oData: OData): Promise<IPrAccountAssignmentTemplateEntity[]> {
    const select = [
        PrAccountAssignmentTemplateEntity.AccountingGroupSelectionCode,
        PrAccountAssignmentTemplateEntity.ItemTypeCode,
        PrAccountAssignmentTemplateEntity.CreditAccountNumber,
        PrAccountAssignmentTemplateEntity.DebitAccountNumber,
        PrAccountAssignmentTemplateEntity.Name,
        PrAccountAssignmentTemplateEntity.AccountingCategoryCode
    ];

    try {
        const result = await oData.getEntitySetWrapper(EntitySetName.PrAccountAssignmentTemplates).query()
            .select(select.join(", "))
            .expand(PrAccountAssignmentTemplateEntity.AccountingGroupSelection)
            .fetchData<IPrAccountAssignmentTemplateEntity[]>();
        return result.value;
    } catch (error) {
        logger.error(error);
        return [];
    }
}

export async function loadPrAccountAssignmentTemplates(storage: FormStorage<IPrPayrollSettingEntity, IPrAccountingStorageCustomData>): Promise<void> {
    const prAccountAssignmentTemplates = await fetchPrAccountAssignmentTemplates(storage.oData);
    storage.setCustomData({ prAccountAssignmentTemplates });
}

export function getTranslationsForPlaceholders(prefix: string, keys: string[]): TRecordString {
    const ret: TRecordString = {};
    keys.forEach(key => {
        ret[key] = `<${i18next.t(`PrAccountingSettings:${prefix}.${key}`)}>`;
    });
    return ret;
}

export function getGroupSelectionReplacements(): TRecordString {
    return getTranslationsForPlaceholders("GroupSelection", ["EmployeeNumber", "EmployeeLastName", "EmployeeFirstName", "PayrollYear", "PayrollMonth"]);
}

export function getAccountingTextReplacements(itemTypeCode: PrAccountingItemTypeCode): TRecordString {
    const keys: string[] = [];
    switch (itemTypeCode) {
        // todo: add more cases
        // case PrAccountingItemTypeCode.WithholdingTax:
        //     keys.push("Id");
        //     break;
    }

    return getTranslationsForPlaceholders(itemTypeCode, keys);
}

export function getAccountingText(storage: FormStorage<IPrPayrollSettingEntity, IPrAccountingStorageCustomData>, bindingContext: BindingContext): {
    bindingContext: BindingContext, value?: string
} {
    let value: string = null;

    const groupSelCode = storage.getValue(bindingContext.navigate(PrAccountAssignmentEntity.AccountingGroupSelectionCode));
    const itemTypeCode: PrAccountingItemTypeCode = storage.getValue(bindingContext.navigate(PrAccountAssignmentEntity.ItemTypeCode));
    const name = storage.getValue(bindingContext.navigate(PrAccountAssignmentEntity.Name));

    if (name && groupSelCode) {
        const groupSelectionDescription = i18next.t(`Payroll:GroupSelection.${groupSelCode}`, getGroupSelectionReplacements());
        const key = `Payroll:ItemType.${itemTypeCode}`;
        value = i18next.t(i18next.exists(key) ? key : "Payroll:AccountAssignment.Description", {
            ...getAccountingTextReplacements(itemTypeCode),
            ItemName: name,
            GroupSelection: groupSelectionDescription
        }).toString();
    }

    return {
        bindingContext: bindingContext.navigate(AccountingTextLocalPath),
        value
    };
}

export const AccountTypePath = BindingContext.localContext("AccountTypeTabs");

export function renderAccountingCategoryTabs(args: IFieldDefFn): React.ReactElement {
    const selectedTabId = args.storage.getValueByPath(AccountTypePath) ?? PrAccountingCategoryCode.Employee;

    const entity = args.storage.data.entity as IPrPayrollSettingEntity;
    const assignments = entity?.AccountAssignments ?? [];
    const rootBc = args.storage.data.bindingContext;
    const collectionBc = rootBc?.navigate(PrPayrollSettingEntity.AccountAssignments);
    const additionalFieldData = args.storage.data.additionalFieldData ?? {};

    const tabsWithErrors = new Set<string>();
    if (collectionBc) {
        for (const assignment of assignments) {
            if (hasNestedError(additionalFieldData, collectionBc.addKey(assignment)) && assignment.AccountingCategoryCode) {
                tabsWithErrors.add(assignment.AccountingCategoryCode);
            }
        }
    }

    const data = Object.values(PrAccountingCategoryCode).reverse().map(code => ({
        id: code,
        title: i18next.t(`PrAccountingSettings:AccountingCategory.${code}`),
        hasError: tabsWithErrors.has(code)
    }));
    return <Tabs data={data} selectedTabId={selectedTabId}
                 onChange={(selectedTabId) => {
                     args.storage.setValueByPath(AccountTypePath, selectedTabId);
                     args.storage.refresh();
                 }}/>;
}